import {
  FARM_DIAGNOSTICS_COMPARISON_COLUMN_3YR_AVG,
  FARM_DIAGNOSTICS_COMPARISON_COLUMN_CHANGE,
  FARM_DIAGNOSTICS_COMPARISON_COLUMN_LATEST,
  FARM_DIAGNOSTICS_COMPARISON_TABLE_ROW_LABELS,
  FARM_DIAGNOSTICS_FINANCIAL_RUE_TOOLTIP,
  FARM_DIAGNOSTICS_GHOST_STATE_MESSAGE,
  FARM_DIAGNOSTICS_RUE_TOOLTIP,
} from 'constants/uiConstants';
import TermTooltip from 'components/common/TermTooltip/TermTooltip';
import React, { useMemo } from 'react';
import { formatCurrency } from 'utils/CurrencyUtils';
import { formatNumber } from '../Insights/summary2/formatNumber';
import SectionCard from '../Insights/summary2/SectionCard';
import ChartGhostState from './ChartGhostState';
import {
  computeFinancialTrendFactors,
  computeKeyMetricsSummary,
  computeRollingAverage3yr,
} from './computeFarmDiagnosticsMetrics';
import TrendChart from './TrendChart';
import TrendKeyMetricsPanel from './TrendKeyMetricsPanel';
import type { FarmDiagnosticsSeries } from './useFarmDiagnosticsSeries';

interface FinancialEfficiencyDashboardProps {
  series: FarmDiagnosticsSeries;
  showYearOnYear: boolean;
  showRollingAvg: boolean;
  showMarkers: boolean;
  /** Overrides the empty-chart prompt — the plan preview points to the workspace instead of a Yearly Overview it doesn't have. */
  ghostMessage?: string;
}

const formatFinancialWue = (v: number) => `${formatCurrency(v)}/mm`;

/** Hovers explaining RUE on the comparison rows that use it. */
const COMPARISON_ROW_TOOLTIPS: Record<string, string> = {
  financialWue: FARM_DIAGNOSTICS_FINANCIAL_RUE_TOOLTIP,
  wueIndex: FARM_DIAGNOSTICS_RUE_TOOLTIP,
};

interface ComparisonRow {
  key: string;
  label: string;
  threeYrAvg: number | null;
  latest: number | null;
  change: number | null;
  formatValue: (v: number) => string;
}

const ComparisonTableRow: React.FC<{ row: ComparisonRow }> = ({ row }) => {
  const changeColorClass =
    row.change == null
      ? 'text-charcoal'
      : row.change > 0
        ? 'text-success'
        : row.change < 0
          ? 'text-danger'
          : 'text-charcoal';

  return (
    <tr className="hover:bg-rowHover">
      <td className="py-1.5 pl-2 pr-6 text-base text-charcoal">
        <TermTooltip tooltip={COMPARISON_ROW_TOOLTIPS[row.key]}>{row.label}</TermTooltip>
      </td>
      <td className="w-32 px-2 py-1.5 text-right tabular-nums text-charcoal">
        {row.threeYrAvg != null ? row.formatValue(row.threeYrAvg) : '—'}
      </td>
      <td className="w-32 px-2 py-1.5 text-right tabular-nums text-charcoal">
        {row.latest != null ? row.formatValue(row.latest) : '—'}
      </td>
      <td className={`w-32 px-2 py-1.5 text-right tabular-nums ${changeColorClass}`}>
        {row.change != null
          ? `${row.change > 0 ? '+' : ''}${row.formatValue(row.change)}`
          : '—'}
      </td>
    </tr>
  );
};

/** Dashboard 3 — Financial WUE trend + a 3-yr-avg/latest/change comparison table for the selected paddock. */
const FinancialEfficiencyDashboard: React.FC<FinancialEfficiencyDashboardProps> = ({
  series,
  showYearOnYear,
  showRollingAvg,
  showMarkers,
  ghostMessage = FARM_DIAGNOSTICS_GHOST_STATE_MESSAGE,
}) => {
  const points = useMemo(
    () => series.years.map((y) => ({ year: y.year, value: y.financialWue })),
    [series.years]
  );
  const markers = useMemo(
    () => series.years.map((y) => ({ year: y.year, tags: y.practiceTags })),
    [series.years]
  );

  const gmRollingAvg = useMemo(
    () =>
      computeRollingAverage3yr(
        series.years.map((y) => ({ year: y.year, value: y.gmPerHa }))
      ),
    [series.years]
  );

  const latest = series.years[series.years.length - 1];
  const previous = series.years[series.years.length - 2];
  const summary = computeKeyMetricsSummary(
    points,
    series.financialWueRollingAvg,
    series.financialWueTrendByYear
  );
  const factors = computeFinancialTrendFactors(latest, previous);

  const rows: ComparisonRow[] = useMemo(() => {
    if (!latest) return [];

    const build = (
      key: string,
      label: string,
      latestValue: number | null,
      rollingAvg: { year: number; rollingAvg: number | null }[],
      formatValue: (v: number) => string
    ): ComparisonRow => {
      const threeYrAvg =
        rollingAvg.find((p) => p.year === latest.year)?.rollingAvg ?? null;
      return {
        key,
        label,
        threeYrAvg,
        latest: latestValue,
        change:
          threeYrAvg != null && latestValue != null
            ? latestValue - threeYrAvg
            : null,
        formatValue,
      };
    };

    return [
      build(
        'financialWue',
        FARM_DIAGNOSTICS_COMPARISON_TABLE_ROW_LABELS.financialWue,
        latest.financialWue,
        series.financialWueRollingAvg,
        formatFinancialWue
      ),
      build(
        'gmPerHa',
        FARM_DIAGNOSTICS_COMPARISON_TABLE_ROW_LABELS.gmPerHa,
        latest.gmPerHa,
        gmRollingAvg,
        (v) => `${formatCurrency(v)}/ha`
      ),
      build(
        'wueIndex',
        FARM_DIAGNOSTICS_COMPARISON_TABLE_ROW_LABELS.wueIndex,
        latest.wueIndex,
        series.wueRollingAvg,
        (v) => `${formatNumber(v)} kg/mm`
      ),
    ];
  }, [latest, series.financialWueRollingAvg, series.wueRollingAvg, gmRollingAvg]);

  const hasChartData = points.some((p) => p.value != null);
  const hasComparisonData = rows.some((r) => r.latest != null || r.threeYrAvg != null);

  return (
    <div className="flex flex-col gap-6">
      <TrendKeyMetricsPanel
        summary={summary}
        factors={factors}
        formatValue={formatFinancialWue}
        yoyUnit="$/mm"
      />

      <SectionCard
        id="financialEfficiency-chart"
        title="Financial RUE Trend"
        titleTooltip={FARM_DIAGNOSTICS_FINANCIAL_RUE_TOOLTIP}
        variant="result"
      >
        <ChartGhostState active={!hasChartData} message={ghostMessage}>
          <TrendChart
            points={points}
            rollingAvg={series.financialWueRollingAvg}
            trendByYear={series.financialWueTrendByYear}
            markers={showMarkers ? markers : []}
            formatValue={formatFinancialWue}
            valueLabel="Financial RUE ($/mm)"
            valueTooltip={FARM_DIAGNOSTICS_FINANCIAL_RUE_TOOLTIP}
            showYearOnYear={showYearOnYear}
            showRollingAvg={showRollingAvg}
          />
        </ChartGhostState>
      </SectionCard>

      <SectionCard id="financialEfficiency-comparison" title="3-Year Comparison" variant="result">
        <ChartGhostState
          active={!hasComparisonData}
          message={ghostMessage}
        >
          <div className="summary-grid-scroll w-0 min-w-full overflow-x-auto">
            <table className="w-max border-collapse text-base">
              <thead>
                <tr>
                  <th className="pb-2 pl-2 pr-6 text-left text-sm font-bold text-charcoal">
                    Metric
                  </th>
                  <th className="w-32 px-2 pb-2 text-right text-sm font-bold text-charcoal">
                    {FARM_DIAGNOSTICS_COMPARISON_COLUMN_3YR_AVG}
                  </th>
                  <th className="w-32 px-2 pb-2 text-right text-sm font-bold text-charcoal">
                    {FARM_DIAGNOSTICS_COMPARISON_COLUMN_LATEST}
                  </th>
                  <th className="w-32 px-2 pb-2 text-right text-sm font-bold text-charcoal">
                    {FARM_DIAGNOSTICS_COMPARISON_COLUMN_CHANGE}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <ComparisonTableRow key={row.key} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        </ChartGhostState>
      </SectionCard>
    </div>
  );
};

export default FinancialEfficiencyDashboard;
